// Package imports:
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import ISLocale from 'date-fns/locale/is';
import { format } from 'date-fns';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import Label from '../../ui-elements/Label/Label';
// Service imports:
import { formatNumber } from '../../services/utils';
import { SHORT_MONTHS } from '../../services/config';
// Type imports:
import { IDefaultProps } from '../../types/Types';

interface IOwnProps {
    symbol?: string,
    title?: string,
    height?: number,
    data: [] | IVisibleCurrency[]
}

interface IVisibleCurrency {
    symbol: string,
    name: string,
    color: string,
    data: number[][]
}

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const GjaldmidlarChart: React.FC<Props> = ({
    title,
    height = 500,
    accessToken,
    refreshRateMs,
    data,
    ...props
}) => {
    const [selectedRange, setSelectedRange] = useState(4);

    // Ref to the div where we render the chart.
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let allSeries: Highcharts.SeriesLineOptions[] = data.map((currency, i) => {
             return ({
                id: currency.symbol,
                type: 'line',
                name: currency.name,
                color: currency.color,
                data: currency.data ?? []
            })
        })

        Highcharts.setOptions({
            lang: {
                shortMonths: SHORT_MONTHS,
                resetZoom: 'Til baka',
                resetZoomTitle: ''
            }
        });

        if (divRef.current !== null) {
            Highcharts.stockChart(divRef.current, {
                chart: {
                    height: height,
                    marginTop: 80,
                    reflow: true,
                    style: {
                        fontFamily: "Roboto"
                    },
                    zoomType: "xy",
                    selectionMarkerFill: '#4569EE18',
                    resetZoomButton: {
                        position: {
                            x: 0,
                            y: -75
                        },
                        theme: {
                            r: 6,
                            height: 28
                        }
                    },
                    plotBackgroundColor: {
                        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                        stops: [
                            [0.05, '#FFF'],
                            [0.95, '#F8F8FA']
                        ]
                    }
                },
                exporting: {
                    buttons: {
                        contextButton: {
                            x: 30,
                            y: 36
                        }
                    }
                },
                credits: {
                    enabled: false
                },
                title: {
                    text: '',
                    align: 'center',
                    x: 60,
                    margin: -14
                },
                navigator: {
                    enabled: true,
                    adaptToUpdatedData: false,
                    handles: {
                        height: 22,
                        width: 6
                    },
                    maskFill: '#D4DEFF80',
                    series: {
                        lineWidth: 0,
                        opacity: 0
                    },
                    height: 20,
                    xAxis: {
                        labels: {
                            style: {
                                color: '#7B7C83',
                                fontSize: '10px'
                            }
                        },
                        gridLineWidth: 0,
                        minTickInterval: 1000 * 60 * 60 * 24 * 365,
                        lineWidth: 0
                    },
                    yAxis: {
                        lineWidth: 0
                    },
                    outlineWidth: 0
                },
                scrollbar: {
                    enabled: false
                },
                tooltip: {
                    enabled: true,
                    formatter: function() {
                        //filtera einungis það sem er visible af því ef það er notað points[0] crashar allt þegar eitthvað series er disable-að
                        return (
                            `<div class="chart-tooltip">
                                <p class="tooltip-date">${format(this.x, 'EEEE, dd. MMM yyyy', {locale: ISLocale})}</p>
                                ${this.points?.filter(x => x.point.visible === true).map(x => (
                                    `<p><span style="color: ${x.color}">${x.series.getName()}</span>: ${formatNumber(x.y, '-', 3)}</p>`
                                ))}
                            </div>`
                        ).replace(/>,</g,'><')
                    },
                    borderWidth: 0,
                    shadow: false,
                    useHTML: true,
                    shared: true
                },
                xAxis: [{
                    type: 'datetime',
                    title: {
                        text: null
                    },
                    minTickInterval: 1000 * 60 * 60 * 24 * 30,
                    tickWidth: 0
                }],
                yAxis: [{
                    title: {
                        text: 'Kross',
                        margin: 15
                    },
                    minTickInterval: 1,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false,
                    height: '100%',
                }
                ],
                plotOptions: {
                    series: {
                        color: '#4569EE',
                        enableMouseTracking: true,
                        marker: {
                            enabled: false
                        },
                        animation: false,
                        turboThreshold: 0,
                        compare: (allSeries.length > 1) ? 'percent' : undefined
                    }
                },
                legend: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'top',
                    itemStyle: {
                        color: '#232530',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '26px'
                    },
                    y: 10
                },
                rangeSelector: {
                    allButtonsEnabled: true,
                    buttonPosition: {
                        align: 'right',
                        x: 0,
                        y: -80
                    },
                    buttons: [{
                        type: 'month',
                        count: 1,
                        text: '1 mán.',
                        title: '1 mánuður',
                        events: {
                            click: () => setSelectedRange(0)
                        }
                    }, {
                        type: 'month',
                        count: 3,
                        text: '3 mán.',
                        title: '3 mánuðir',
                        events: {
                            click: () => setSelectedRange(1)
                        }
                    }, {
                        type: 'month',
                        count: 6,
                        text: '6 mán.',
                        title: '6 mánuðir',
                        events: {
                            click: () => setSelectedRange(2)
                        }
                    }, {
                        type: 'ytd',
                        text: 'Þetta ár',
                        title: 'Þetta ár',
                        events: {
                            click: () => setSelectedRange(3)
                        }
                    }, {
                        type: 'year',
                        count: 1,
                        text: '1 ár',
                        title: '1 ár',
                        events: {
                            click: () => setSelectedRange(4)
                        }
                    }, {
                        type: 'year',
                        count: 3,
                        text: '3 ár',
                        title: '3 ár',
                        events: {
                            click: () => setSelectedRange(5)
                        }
                    }, {
                        type: 'year',
                        count: 5,
                        text: '5 ár',
                        title: '5 ár',
                        events: {
                            click: () => setSelectedRange(6)
                        }
                    }, 
                ],
                    buttonSpacing: 5,
                    buttonTheme: {
                        r: 6,
                        height: 25,
                        width: 50
                    },
                    inputEnabled: false,
                    selected: selectedRange,
                    labelStyle: {
                        display: 'none'
                    },
                    dropdown: 'never'
                },
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: 480
                        },
                        chartOptions: {
                            rangeSelector: {
                                dropdown: 'always'
                            }
                        }
                    }]
                },
                series: allSeries
            }, () => {});
        }
    }, [data, divRef.current]);

    return (
        <DisplayBox
            className='KCL_chart'
            title={title}
            {...props}
        >
            <div className='row'>
                <div className="chart" ref={divRef}></div>
            </div>
            <a className="label-wrap" href="https://sedlabanki.is" target="_blank" rel="noopener noreferrer">
                <Label text="Seðlabanki Íslands" labelType="origin" />
            </a>
        </DisplayBox>
    );
}

export default GjaldmidlarChart;